/* eslint-disable max-lines-per-function */
import cx from 'classnames';
import {useContext, useEffect} from 'react';
import {createPortal} from 'react-dom';
import {useSelector, useStore} from 'react-redux';
import {useCurrentRoute} from '~/CreateFabricInvitation/navigation';
import {eventInEditMode} from '~/CreateFabricInvitation/reducers/details/selectors';
import {
  selectCurrentSection,
  selectIsFullDyo,
  selectIsPristineUyo,
  selectIsUyo,
  selectPremiumFeaturesPristine,
  selectSavingStep,
  selectUsedPremiumFeature,
  selectAllBackgroundsLoaded,
  selectPristineEnvelopeDesign,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {
  selectIsPremium,
  selectIsPremiumTemplate,
} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {selectIsUpsellConfirmed} from '~/CreateFabricInvitation/reducers/modals';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {ExitPremiumPreviewModal} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/ExitPremiumPreviewModal/ExitPremiumPreviewModal';
import {Sections} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {Button} from '~library/atoms/Button';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import {getEventId} from '~/common/fabric/reducers/eviteEvent/selectors';
import {useIsExtraLargeAndUp, useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {NavigationButtons} from './components/NavigationButtons';
import {useModals} from './hooks/useModals';
import {useNavigation} from './hooks/useNavigation';
import {useUYO} from './hooks/useUYO';
import {useNavigator} from './hooks/useNavigator';

import './NavBottom.sass';

export const NavBottom = ({editingMobile}) => {
  const store = useStore();
  const isPremium = useSelector(selectIsPremium);
  const isPremiumTemplate = useSelector(selectIsPremiumTemplate);
  const upsellConfirmed = useSelector(selectIsUpsellConfirmed);
  const inEditMode = useSelector(eventInEditMode);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const premiumFeaturesPristine = useSelector(selectPremiumFeaturesPristine);
  const savingStep = useSelector(selectSavingStep);
  const pristineEnvelopeDesign = useSelector(selectPristineEnvelopeDesign);
  const eventId = useSelector(getEventId);
  const isUyo = useSelector(selectIsUyo);
  const isFullDyo = useSelector(selectIsFullDyo);
  const currentSection = useSelector(selectCurrentSection);
  const isPristineUyo = useSelector(selectIsPristineUyo);

  const currentRoute = useCurrentRoute();
  const {canvas} = useContext(EventCreateContext);

  const isScreenExtraLarge = useIsExtraLargeAndUp();
  const isLargishAndUp = useIsLargishAndUp();
  const allBackgroundsLoaded = useSelector(selectAllBackgroundsLoaded);

  useEffect(() => {
    localStorage.removeItem('tooltip-id-to-hide');
  }, []);

  const navigate = useNavigator({
    isMobile: !isLargishAndUp,
    disableAutoOpenEditor: currentSection === Sections.CARD_DESIGN,
  });

  const {
    showUpsellModal,
    upsellModalMode,
    showExitPremiumPreviewModal,
    setShowUpsellModal,
    setShowExitPremiumPreviewModal,
    upgradeToPremium,
    downgradeBackToBasic,
    handleShowUpsell,
  } = useModals({navigate});

  const {handleNavigation} = useNavigation({
    store,
    canvas,
    currentRoute,
    inEditMode,
    usedPremiumFeature,
    premiumFeaturesPristine,
    handleShowUpsell,
  });

  const {getUYOImageInfo, handleImageUploaded} = useUYO({canvas});
  const {id} = getUYOImageInfo();

  const upgradeAndContinue = async () => {
    upgradeToPremium();
    await handleNavigation();
  };

  return (
    <div
      className={cx('create-design__nav-bottom', {
        'create-design__nav-bottom--hidden': editingMobile,
      })}
    >
      <NavigationButtons
        isUyo={isUyo}
        isFullDyo={isFullDyo}
        isPristineUyo={isPristineUyo}
        isPremium={isPremium}
        isScreenExtraLarge={isScreenExtraLarge}
        isLargishAndUp={isLargishAndUp}
        pristineEnvelopeDesign={pristineEnvelopeDesign}
        eventId={eventId}
        handleImageUploaded={handleImageUploaded}
        id={id}
      />

      <div className="create-design__nav-bottom__cta_wrapper">
        <Button
          id="navBottom"
          className="create-design__nav-bottom__next"
          data-qa-id="nav-bottom-next"
          variant="primary"
          size="large"
          disabled={!allBackgroundsLoaded}
          onClick={handleNavigation}
        >
          {inEditMode && !isPremiumTemplate && isPremium && upsellConfirmed
            ? 'Next: Checkout'
            : currentRoute.nextButtonText}
        </Button>
      </div>

      {showUpsellModal && (
        <UpsellModal
          noAutoRestore
          toggleUpsellModal={() => setShowUpsellModal(!showUpsellModal)}
          closeOverlay={() => {
            setShowUpsellModal(false);
            setShowExitPremiumPreviewModal(true);
          }}
          onConfirmAction={upgradeAndContinue}
          mode={upsellModalMode}
        />
      )}

      {showExitPremiumPreviewModal &&
        createPortal(
          <ExitPremiumPreviewModal
            closeOverlay={() => {
              setShowExitPremiumPreviewModal(false);
            }}
            onConfirmAction={downgradeBackToBasic}
          />,
          document.getElementById('pbModalRoot')
        )}

      {createPortal(
        <LoadingOverlay loadingMessage="Saving..." show={savingStep} />,
        document.getElementById('fabric-create-invitation-root')
      )}
    </div>
  );
};
